import {useMemo} from "react";
import WideDrawer from "../../general/WideDrawer.tsx";
import {MoneyLabel} from "../../general/MoneyLabel.tsx";
import type {DrillDownLine} from "./types/types.ts";
import {toCentsFromMoney, type Money} from "../../../lib/Money.ts";
import {SignedMoneyCell} from "../trialBalance/components/StatementTable/AmountCells.tsx";
import {
    ArrowsRightLeftIcon,
    CalculatorIcon,
    CubeIcon,
    DocumentTextIcon,
    LinkIcon
} from "@heroicons/react/24/outline";
import {createTable, type TableHeaderSpec} from "../../table/CreateTable.tsx";
import {DEFAULT_LOCALE} from "../../../types/constants.ts";
import {cleanDocumentFileName} from "../../../lib/TFICLib.ts";
import {useDocumentDrawer} from "../../../views/DocumentDrawer";
import type {PresentationSourceType} from "../../../generated/enums.ts";

const typeMap: Record<PresentationSourceType, { label: string, icon: any }> = {
    BILL: { label: 'Factuur', icon: DocumentTextIcon },
    TRANSACTION: { label: 'Transactie', icon: ArrowsRightLeftIcon },
    MANUAL_BOOKING: { label: 'Memoriaal', icon: CalculatorIcon },
    FIXED_ASSET: { label: 'Vast actief', icon: CubeIcon },
    MATCH: { label: 'Betaling', icon: LinkIcon },
};

function getSourceLabel(line: DrillDownLine): string {
    return line.sources[0]?.description || line.description || "Geen beschrijving";
}

function getSourceDetail(line: DrillDownLine): string {
    const s = line.sources[0];
    if (!s) return "";
    switch (s.source_type_for_presentation) {
        case 'BILL':
            return s.file_name ? cleanDocumentFileName(s.file_name) : "—";
        default:
            return "";
    }
}

function getDocumentId(line: DrillDownLine): string | null {
    const s = line.sources[0];
    if (!s) return null;
    switch (s.source_type_for_presentation) {
        case 'BILL':
            return s.document_id || null;
        default:
            return null;
    }
}

function getTransactionId(line: DrillDownLine): string | null {
    const s = line.sources[0];
    if (s?.source_type_for_presentation === 'TRANSACTION') {
        return s.transaction_id;
    }
    return null;
}

function isRowClickable(line: DrillDownLine, canOpenTransaction: boolean): boolean {
    return getDocumentId(line) != null || (canOpenTransaction && getTransactionId(line) != null);
}

export interface DrillDownDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    businessId: string;
    emptyMessage?: string;
    rows: DrillDownLine[];
    headingPrefix?: string;
    onTransactionClick?: (transactionId: string) => void;
    openingBalance?: Money;
}

export function DrillDownDrawer({
    open,
    onClose,
    title,
    emptyMessage = "Er zijn geen bronnen om te tonen voor deze rubriek.",
    rows,
    businessId,
    headingPrefix = "Bronnen",
    onTransactionClick,
    openingBalance,
}: DrillDownDrawerProps) {
    const openingCents = openingBalance ? toCentsFromMoney(openingBalance) : 0;
    const headingText = title ? `${headingPrefix} – ${title}` : headingPrefix;
    const documentDrawer = useDocumentDrawer();

    const totals = useMemo(() => {
        let debitSum = 0;
        let creditSum = 0;
        for (const row of rows) {
            debitSum += row.debit_amount.signed_amount;
            creditSum += row.credit_amount.signed_amount;
        }
        const currency =
            rows[0]?.debit_amount.currency || rows[0]?.credit_amount.currency;
        return { debit: debitSum, credit: creditSum, currency };
    }, [rows]);

type DrillDownTableRow = DrillDownLine & { id: string };

    const identifiableSortedRows = useMemo(() => {
        const toTimestamp = (line: DrillDownLine): number => {
            const dateStr = line.sources[0]?.source_date;
            if (!dateStr)
                return Number.MAX_SAFE_INTEGER;

            const timestamp = new Date(dateStr).getTime();
            return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
        };

        const rowsWithIds: DrillDownTableRow[] = rows.map((line, index) => ({
            ...line,
            id: line.sources[0]?.resource_id || `row-${index}`
        }));

        return rowsWithIds.sort((a, b) => toTimestamp(a) - toTimestamp(b));
    }, [rows]);

    const tableSpec = useMemo<TableHeaderSpec<DrillDownTableRow>>(() => ({
        columns: [
            {
                field: "type",
                name: "Type",
                customRender: (line) => {
                    const type = line.sources[0]?.source_type_for_presentation;
                    const config = typeMap[type] || { label: 'Onbekend', icon: DocumentTextIcon };
                    const Icon = config.icon;
                    return (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200/60">
                            <Icon className="size-3.5 shrink-0" aria-hidden />
                            {config.label}
                        </span>
                    );
                },
            },
            {
                field: "details",
                name: "Details",
                style: { widthMultiplier: 1.5 },
                customRender: (row) => {
                    const detail = getSourceDetail(row);
                    const isClickable = isRowClickable(row, !!onTransactionClick);
                    return (
                        <div className={isClickable ? "cursor-pointer hover:text-blue-600" : ""}>
                            <p className="whitespace-normal text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">{getSourceLabel(row)}</p>
                            {detail && <p className="whitespace-normal text-xs text-gray-500 [overflow-wrap:anywhere]">{detail}</p>}
                        </div>
                    );
                },
            },
            {
                field: "source_date",
                name: "Datum",
                customRender: (line) => {
                    const dateStr = line.sources[0]?.source_date;
                    return dateStr
                        ? <span>{new Date(dateStr).toLocaleDateString(DEFAULT_LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                        : <span>—</span>;
                },
            },
            {
                field: "debit",
                name: "Debet",
                customRender: (row) => {
                    const cents = row.debit_amount.signed_amount;
                    return cents > 0
                        ? (
                            <span className="whitespace-nowrap font-medium text-gray-900">
                                <MoneyLabel
                                    value={cents / 100}
                                    currency={row.debit_amount.currency}
                                    showCurrency={true}
                                />
                            </span>
                        )
                        : <span>—</span>;
                },
            },
            {
                field: "credit",
                name: "Credit",
                customRender: (row) => {
                    const cents = row.credit_amount.signed_amount;
                    return cents > 0
                        ? (
                            <span className="whitespace-nowrap font-medium text-gray-900">
                                <MoneyLabel
                                    value={cents / 100}
                                    currency={row.credit_amount.currency}
                                    showCurrency={true}
                                />
                            </span>
                        )
                        : <span>—</span>;
                },
            },
        ],
        onRowClick: (row) => {
            const docId = getDocumentId(row);
            if (docId) {
                documentDrawer.open(businessId, docId);
                return;
            }
            const transactionId = getTransactionId(row);
            if (transactionId && onTransactionClick) {
                onTransactionClick(transactionId);
            }
        },
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: true,
            allowFullRowClick: true,
        },
    }), [businessId, documentDrawer, onTransactionClick]);

    const Table = useMemo(() => createTable(identifiableSortedRows, tableSpec), [identifiableSortedRows, tableSpec]);

    return (
        <WideDrawer open={open} updateOpenState={onClose} title={headingText} width="md">
            <div className="flex flex-col h-full">
                {rows.length === 0 && openingCents === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-50 mb-4">
                            <DocumentTextIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                        </div>
                        <h3 className="text-sm font-medium text-gray-900">Geen bronnen gevonden</h3>
                        <p className="mt-1 text-sm text-gray-500">{emptyMessage}</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between py-3 border-b border-gray-200 mb-4">
                            <span className="text-sm text-gray-500">
                                {rows.length} {rows.length === 1 ? "boeking" : "boekingen"}
                            </span>
                            <div className="flex gap-4 text-sm font-semibold text-gray-900">
                                {openingBalance && openingCents !== 0 && (
                                    <span className="inline-flex gap-1">
                                        Beginbalans: <SignedMoneyCell money={openingBalance} />
                                    </span>
                                )}
                                {totals.debit > 0 && totals.currency && (
                                    <span>
                                        Debet:{" "}
                                        <MoneyLabel
                                            value={totals.debit / 100}
                                            currency={totals.currency}
                                            showCurrency={true}
                                        />
                                    </span>
                                )}
                                {totals.credit > 0 && totals.currency && (
                                    <span>
                                        Credit:{" "}
                                        <MoneyLabel
                                            value={totals.credit / 100}
                                            currency={totals.currency}
                                            showCurrency={true}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 -mx-4 sm:-mx-6 overflow-auto">
                            <Table />
                        </div>
                    </>
                )}
            </div>
        </WideDrawer>
    );
}
